/**
 * FunnelStoryCard Component
 * Displays the sales funnel with conversion rates (Current vs 6-Month Average)
 * Created: 2025-11-17
 */

import React, { useMemo } from 'react';
import {
  calculate6MonthRollingAverage,
  calculateConversionRates,
  getBusinessDaysInHistoricalMonth,
  getLastNCompleteMonths
} from '../utils/trendingCalculations';

export default function FunnelStoryCard({ data, selectedRep, month, reps, developerMode }) {
  // Calculate business days elapsed in current month
  const businessDaysElapsed = useMemo(() => {
    const today = new Date();
    const year = today.getFullYear();
    const currentMonth = today.getMonth();

    let businessDays = 0;
    for (let day = 1; day <= today.getDate(); day++) {
      const date = new Date(year, currentMonth, day);
      const dayOfWeek = date.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        businessDays++;
      }
    }
    return businessDays;
  }, []);

  // Current month conversion rates
  const currentRates = useMemo(() => {
    return calculateConversionRates(data, selectedRep, month);
  }, [data, selectedRep, month]);

  // 6-month rolling average conversion rates
  const historicalRates = useMemo(() => {
    const last6Months = getLastNCompleteMonths(month, 6);
    let totalCallsToApps = 0;
    let totalSmsToApps = 0;
    let totalAppsToApproved = 0;
    let totalApprovedToFunded = 0;
    let totalAppsToFunded = 0;
    let totalAvgDealSize = 0;
    let monthsCount = 0;

    last6Months.forEach(m => {
      const rates = calculateConversionRates(data, selectedRep, m);
      if (rates.counts.apps > 0) { // Only include months with data
        totalCallsToApps += rates.callsToApps;
        totalSmsToApps += rates.smsToApps;
        totalAppsToApproved += rates.appsToApproved;
        totalApprovedToFunded += rates.approvedToFunded;
        totalAppsToFunded += rates.appsToFunded;
        totalAvgDealSize += rates.avgDealSize;
        monthsCount++;
      }
    });

    if (monthsCount === 0) {
      return {
        callsToApps: 0,
        smsToApps: 0,
        appsToApproved: 0,
        approvedToFunded: 0,
        appsToFunded: 0,
        avgDealSize: 0
      };
    }

    return {
      callsToApps: Math.round((totalCallsToApps / monthsCount) * 10) / 10,
      smsToApps: Math.round((totalSmsToApps / monthsCount) * 10) / 10,
      appsToApproved: Math.round((totalAppsToApproved / monthsCount) * 10) / 10,
      approvedToFunded: Math.round((totalApprovedToFunded / monthsCount) * 10) / 10,
      appsToFunded: Math.round((totalAppsToFunded / monthsCount) * 10) / 10,
      avgDealSize: Math.round((totalAvgDealSize / monthsCount) * 100) / 100
    };
  }, [data, selectedRep, month]);

  // Calculate variances
  const getVariance = (current, historical) => {
    const diff = current - historical;
    const diffRounded = Math.round(diff * 10) / 10;

    let indicator = '→';
    let color = '#64748b'; // Gray (neutral)

    if (diff > 0.5) {
      indicator = '↗';
      color = '#10b981'; // Green (positive)
    } else if (diff < -0.5) {
      indicator = '↘';
      color = '#ef4444'; // Red (negative)
    }

    return {
      diff: diffRounded,
      indicator,
      color,
      text: diff > 0 ? `+${diffRounded}pp` : `${diffRounded}pp`
    };
  };

  const { calls, sms, apps, approved, funded, revenue } = currentRates.counts;

  return (
    <div style={{
      backgroundColor: 'white',
      padding: '20px',
      borderRadius: '8px',
      marginBottom: '20px',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
      border: '1px solid #e2e8f0',
      position: 'relative'
    }}>
      {/* Developer Mode: Section Label */}
      {developerMode && (
        <div className="dev-section-label">S-2</div>
      )}
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        marginBottom: '16px',
        paddingBottom: '12px',
        borderBottom: '2px solid #e2e8f0'
      }}>
        <span style={{ fontSize: '20px', marginRight: '8px' }}>🔄</span>
        <h3 style={{
          margin: 0,
          fontSize: '18px',
          fontWeight: 700,
          color: '#1e293b'
        }}>
          The Funnel Story
        </h3>
        <span style={{
          marginLeft: 'auto',
          fontSize: '13px',
          color: '#64748b',
          fontWeight: 500
        }}>
          Current vs 6-Month Avg
        </span>
      </div>

      {/* Funnel Visual */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-around',
        alignItems: 'center',
        marginBottom: '24px',
        padding: '16px 0'
      }}>
        {/* Calls */}
        <FunnelStage
          label="CALLS"
          value={calls}
          color="#3b82f6"
        />

        <FunnelArrow />

        {/* Apps */}
        <FunnelStage
          label="APPS"
          value={apps}
          color="#10b981"
        />

        <FunnelArrow />

        {/* Approved */}
        <FunnelStage
          label="APPROVED"
          value={approved}
          color="#8b5cf6"
        />

        <FunnelArrow />

        {/* Funded */}
        <FunnelStage
          label="FUNDED"
          value={funded}
          color="#f59e0b"
        />
      </div>

      {/* Conversion Rates */}
      <div style={{
        backgroundColor: '#f8fafc',
        padding: '16px',
        borderRadius: '6px',
        marginBottom: '16px'
      }}>
        <div style={{
          fontSize: '13px',
          fontWeight: 600,
          color: '#475569',
          marginBottom: '12px',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}>
          Conversion Rates (Current → 6-Mo Avg)
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
          <ConversionRate
            label="Calls → Apps"
            current={currentRates.callsToApps}
            historical={historicalRates.callsToApps}
            variance={getVariance(currentRates.callsToApps, historicalRates.callsToApps)}
          />

          <ConversionRate
            label="SMS → Apps"
            current={currentRates.smsToApps}
            historical={historicalRates.smsToApps}
            variance={getVariance(currentRates.smsToApps, historicalRates.smsToApps)}
          />

          <ConversionRate
            label="Apps → Approved"
            current={currentRates.appsToApproved}
            historical={historicalRates.appsToApproved}
            variance={getVariance(currentRates.appsToApproved, historicalRates.appsToApproved)}
          />

          <ConversionRate
            label="Approved → Funded"
            current={currentRates.approvedToFunded}
            historical={historicalRates.approvedToFunded}
            variance={getVariance(currentRates.approvedToFunded, historicalRates.approvedToFunded)}
          />
        </div>
      </div>

      {/* Revenue Summary */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        padding: '12px 16px',
        backgroundColor: '#f0fdf4',
        border: '1px solid #86efac',
        borderRadius: '6px'
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '13px', color: '#166534', fontWeight: 500 }}>
            Total Revenue
          </div>
          <div style={{ fontSize: '24px', fontWeight: 700, color: '#15803d' }}>
            ${(revenue / 1000).toFixed(1)}K
          </div>
        </div>

        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ fontSize: '13px', color: '#166534', fontWeight: 500 }}>
            Deals Closed
          </div>
          <div style={{ fontSize: '24px', fontWeight: 700, color: '#15803d' }}>
            {funded}
          </div>
        </div>

        <div style={{ flex: 1, textAlign: 'right' }}>
          <div style={{ fontSize: '13px', color: '#166534', fontWeight: 500 }}>
            Avg Deal Size
          </div>
          <div style={{ fontSize: '20px', fontWeight: 700, color: '#15803d' }}>
            ${(currentRates.avgDealSize / 1000).toFixed(1)}K
            <span style={{
              fontSize: '14px',
              marginLeft: '6px',
              color: getVariance(currentRates.avgDealSize, historicalRates.avgDealSize).color
            }}>
              {getVariance(currentRates.avgDealSize, historicalRates.avgDealSize).indicator}
            </span>
          </div>
        </div>
      </div>

      {/* Quick Insights */}
      <div style={{ marginTop: '16px', fontSize: '13px', color: '#64748b' }}>
        {getQuickInsight(currentRates, historicalRates)}
      </div>
    </div>
  );
}

/**
 * Funnel Stage Component
 */
function FunnelStage({ label, value, color }) {
  return (
    <div style={{
      textAlign: 'center',
      minWidth: '80px'
    }}>
      <div style={{
        backgroundColor: color,
        color: 'white',
        padding: '12px 16px',
        borderRadius: '8px',
        fontWeight: 700,
        fontSize: '22px',
        marginBottom: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        {value}
      </div>
      <div style={{
        fontSize: '11px',
        fontWeight: 600,
        color: '#475569',
        textTransform: 'uppercase',
        letterSpacing: '0.5px'
      }}>
        {label}
      </div>
    </div>
  );
}

/**
 * Funnel Arrow Component
 */
function FunnelArrow() {
  return (
    <div style={{
      fontSize: '24px',
      color: '#cbd5e1',
      margin: '0 8px'
    }}>
      →
    </div>
  );
}

/**
 * Conversion Rate Component
 */
function ConversionRate({ label, current, historical, variance }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    }}>
      <div style={{
        fontSize: '13px',
        fontWeight: 500,
        color: '#475569',
        whiteSpace: 'nowrap'
      }}>
        {label}:
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <span style={{
          fontSize: '15px',
          fontWeight: 700,
          color: '#1e293b'
        }}>
          {current.toFixed(1)}%
        </span>
        <span style={{
          fontSize: '14px',
          fontWeight: 600,
          color: variance.color
        }}>
          {variance.indicator}
        </span>
        <span style={{
          fontSize: '12px',
          color: '#64748b'
        }}>
          (vs {historical.toFixed(1)}%)
        </span>
      </div>
    </div>
  );
}

/**
 * Generate quick insight based on conversion rates
 */
function getQuickInsight(currentRates, historicalRates) {
  const callsToAppsChange = currentRates.callsToApps - historicalRates.callsToApps;
  const approvedToFundedChange = currentRates.approvedToFunded - historicalRates.approvedToFunded;

  if (callsToAppsChange > 1 && approvedToFundedChange > 1) {
    return '✅ Converting better at both top and bottom of funnel';
  } else if (callsToAppsChange > 1 && approvedToFundedChange < -1) {
    return '⚠️ Converting better at top (Calls→Apps) but losing deals at approval stage';
  } else if (callsToAppsChange < -1 && approvedToFundedChange > 1) {
    return '⚠️ Strong close rate (Approved→Funded) but struggling with lead conversion';
  } else if (callsToAppsChange < -1 && approvedToFundedChange < -1) {
    return '🔴 Conversion declining at multiple stages - needs attention';
  } else {
    return '→ Conversion rates are stable compared to 6-month average';
  }
}
